import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "middle-panel" }
const _hoisted_2 = ["slot", "title"]

import { ref, computed, watch } from "vue";
import type { FiBuild } from "../types/build";

interface Props {
  selectedConfig: string | null;
  selectedBuildId: string | null;
  buildData: FiBuild[];
  platform?: Platform;
}

interface Row {
  id: string;
  configId: string;
  selected: boolean;
  cells: {
    timestamp: { value: string };
    "container-version": { value: string };
    platform: { value: string }; // 👈 instead of just `string`
    [key: string]: { value: string };
  };
  buildId: string;
  markedForDeletion?: string | null;
}

// eslint-disable-next-line vue/valid-define-props
type Platform = "android" | "ios" | "all";

const TTL_DAYS = 10;


export default /*@__PURE__*/_defineComponent({
  __name: 'buildVersionsMiddle',
  props: {
    selectedConfig: {},
    selectedBuildId: {},
    buildData: {},
    platform: {}
  },
  emits: ["selected-build-id", "platform-selected"],
  setup(__props: any, { emit: __emit }) {

const headerData = [
  { title: "Timestamp", key: "timestamp" },
  { title: "Version", key: "container-version" },
  { title: "Platform", key: "platform" },
];

const props = __props;

function isPlatform(value: string): value is Platform {
  return value === "all" || value === "ios" || value === "android";
}

const selectedPlatform = ref<Platform>(
  isPlatform(props.platform || "") ? props.platform : "all"
);

function formatTimestamp(isoString: string | null | undefined): string {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const parts = new Intl.DateTimeFormat(undefined, options).formatToParts(date);
  const lookup: { [key: string]: string } = {};
  for (const part of parts) {
    if (part.type !== "literal") {
      lookup[part.type] = part.value;
    }
  }
  return `${lookup.month} ${lookup.day}, ${lookup.year} ${lookup.hour}:${
    lookup.minute
  } ${lookup.dayPeriod || ""}`.trim();
}

// const selectedRowId = ref<string | null>(null);

const filteredBuilds = computed(() => {
  const builds = props.buildData;
  const platform = selectedPlatform.value;

  return builds.filter((row) => {
    const platformValue =
      typeof row.platform === "string"
        ? row.platform.toLowerCase()
        : "unknown";

    const matchesPlatform =
      selectedPlatform.value === "all" ||
      platformValue === platform;

    return matchesPlatform;
  });
});

const buildRows = computed(() => {
  const builds = filteredBuilds.value;

  const rows: Row[] = [];

  builds.forEach((build) => {
      rows.push({
        id: build.buildId,
        selected: build.buildId == props.selectedBuildId,
        configId: build.configId,
        sortTime: build.createdAt,
        cells: {
          timestamp: {
            value: formatTimestamp(build.createdAt) || "N/A",
          },
          "container-version": {
            value: build.buildSettings.containerVersion || "N/A",
          },
          platform: { value: build.platform },
        },
        buildId: build.buildId,
        markedForDeletion: build.markedForDeletion,
      });
  });

  return rows;
});

const pendingDeletionRows = computed(() =>
  buildRows.value.filter((row) => row.markedForDeletion != null)
);

// The TTL index purges the document 10 days after it is stamped, so the tint has
// a deadline worth surfacing — the row is red for a window, not forever.
function pendingDeletionTitle(row: Row): string {
  const markedAt = new Date(row.markedForDeletion as string);
  if (Number.isNaN(markedAt.getTime())) return "Pending deletion";

  const purgeAt = new Date(markedAt);
  purgeAt.setDate(purgeAt.getDate() + TTL_DAYS);

  return `Pending deletion — permanently removed ${formatTimestamp(
    purgeAt.toISOString()
  )} unless restored`;
}

const emit = __emit;

watch(selectedPlatform, (platform) => {
  emit("platform-selected", platform);

  // Check if currently selected row exists in the filtered platform results
  const selectedRowExists = buildRows.value.some(row => row.id === props.selectedBuildId);

  // If selected row doesn't exist in filtered results, select the first row
  if (!selectedRowExists && buildRows.value.length > 0) {
    emit("selected-build-id", buildRows.value[0].id);
  }
});

watch(
  () => props.platform,
  (platform) => {
    if (platform && isPlatform(platform) && platform !== selectedPlatform.value) {
      selectedPlatform.value = platform;
    }
  }
);

function onRowClick(event: CustomEvent<{ row: Row }>) {
  const row = event.detail?.row;

  if (row?.id) {
    emit("selected-build-id", row?.id);
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q2_btn = _resolveComponent("q2-btn")!
  const _component_q2_action_group = _resolveComponent("q2-action-group")!
  const _component_q2_data_table = _resolveComponent("q2-data-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_q2_action_group, {
      "full-width": "true",
      orientation: "horizontal"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q2_btn, {
          intent: selectedPlatform.value === 'all' ? 'workflow-primary' : 'neutral',
          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectedPlatform.value = 'all'))
        }, {
          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
            _createTextVNode(" All ", -1)
          ]))]),
          _: 1
        }, 8, ["intent"]),
        _createVNode(_component_q2_btn, {
          intent: selectedPlatform.value === 'ios' ? 'workflow-primary' : 'neutral',
          onClick: _cache[1] || (_cache[1] = ($event: any) => (selectedPlatform.value = 'ios'))
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode(" iOS ", -1)
          ]))]),
          _: 1
        }, 8, ["intent"]),
        _createVNode(_component_q2_btn, {
          intent: 
            selectedPlatform.value === 'android' ? 'workflow-primary' : 'neutral'
            ,
          onClick: _cache[2] || (_cache[2] = ($event: any) => (selectedPlatform.value = 'android'))
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createTextVNode(" Android ", -1)
          ]))]),
          _: 1
        }, 8, ["intent"])
      ]),
      _: 1
    }),
    _createVNode(_component_q2_data_table, {
      class: "middle-table",
      ".headers": headerData,
      ".rows": buildRows.value,
      "empty-icon": "inbox",
      "empty-message": "No builds found",
      bordered: "horizontal",
      clickable: "true",
      "hide-clickable": "true",
      onClick: onRowClick
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendingDeletionRows.value, (row) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: row.id
          }, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(headerData, (header) => {
              return _createElementVNode("div", {
                key: header.key,
                slot: `row-${row.id}-cell-${header.key}`,
                class: "pending-deletion-cell",
                title: pendingDeletionTitle(row)
              }, _toDisplayString(row.cells[header.key].value), 9, _hoisted_2)
            }), 64))
          ], 64))
        }), 128))
      ]),
      _: 1
    }, 40, [".rows"])
  ]))
}
}

})